import { useContext, useMemo, useState } from 'react'

import { Col, InputNumber, message, Row, Switch } from 'antd'
import { useSelector } from 'react-redux'
import { isArray } from 'lodash'

import { getIdOrNull, isObject } from '../../../utils'
import { Quota, QuotaProperties } from '../../../utils/entities/quota'
import QuickDualDatePickers from '../../../Components/institution/Quotas/shared/QuickDualDatePickers'
import SchoolsSelector from '../../../Components/shared/Selectors/School/SchoolsSelector'
import SectionToYearSelector from '../../../Components/shared/Selectors/SectionToYearSelector'
import SectionsSelector from '../../../Components/shared/Selectors/SectionsSelector'
import SectorsSelector from '../../../Components/shared/Selectors/SectorsSelector'
import { SchoolContext } from '../../SchoolProvider'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import SectionGroupsSelector from '../../../Components/shared/Selectors/SectionGroupsSelector'

import '../../../assets/quotas.scss'

export const QuotaFormModes = Object.freeze({
  NULL: 'QUOTA_NULL',
  DESIDERATA: 'QUOTA_SCHOOL',
  REGULAR: 'QUOTA_INSTITUTION',
  RESTRICTED: 'QUOTA_RESTRICTED'
})

export const BODY_STYLE = { display: 'flex', flexDirection: 'column', height: '93%', width: '100%' }
export const SELECT_STYLE = { width: '200px' }

const { SECTOR, SECTION, SECTION_GROUP, SCHOOL, SECTION_TO_YEAR, START_DATE, END_DATE, TOTAL } = QuotaProperties

const COLSPAN_VALUE = 12

message.config({
  top: 20,
  duration: 3,
  maxCount: 1,
  placement: 'topRight'
})

const QuotaForm = ({ data, disabled, disabledSector, maxTotal = null, mode, studentPathways = [], sectors = [], sectionGroups = {}, onChange }) => {
  const sc = useContext(SchoolContext)

  const t = useSelector(selectTranslate)

  const localSectionGroups = useMemo(() => isObject(data[SECTOR]) ? sectionGroups[getIdOrNull(data[SECTOR])] : [], [sectionGroups, data[SECTOR]])
  const activeStudentPathways = useMemo(() => {
    let actifStudentPathways = []

    if (isObject(studentPathways) && isObject(data.sector)) {
      actifStudentPathways = studentPathways[data.sector.id] ?? []
    } else if (isArray(studentPathways)) {
      actifStudentPathways = isObject(data.sector) && mode === QuotaFormModes.DESIDERATA
        ? studentPathways.filter(sp => !data.section || sp.sectionToYear.section.id === data.section.id)
        : studentPathways
    }

    return actifStudentPathways
  }, [studentPathways, data.sector, data.section, mode])

  const sectionsAndYears = useMemo(() => activeStudentPathways.reduce((acc, { sectionToYear }) => {
    if (acc.years[sectionToYear.section.id]) {
      acc.years[sectionToYear.section.id].push(sectionToYear)
    } else {
      acc.sections.push({ id: sectionToYear.section.id, name: sectionToYear.sectionName, abbreviation: sectionToYear.sectionAbbreviation })
      acc.years[sectionToYear.section.id] = [sectionToYear]
    }

    return acc
  }, { sections: [], years: {} }), [activeStudentPathways])
  const schoolSections = useMemo(() => {
    const schoolSections = isObject(sc) ? sc.sections : { list: [], sectionYears: {} }

    if (localSectionGroups.length > 0) {
      const sectionsMap = localSectionGroups.reduce((acc, group) => {
        group.sections.forEach(s => {
          acc[s.id] = true
        })

        return acc
      }, {})
      schoolSections.list = schoolSections.list.filter(s => !sectionsMap[s.id])
    }

    return schoolSections
  }, [sc?.sections.sectionYears, localSectionGroups])

  const [isYearly, setIsYearly] = useState(false)

  const handleDatesChange = (dates, _) => {
    if (isYearly) {
      onChange({ ...data, startDate: dates[0], endDate: dates[0].clone().add(5, 'years') })
    } else {
      onChange({ ...data, startDate: dates[0], endDate: dates[1] })
    }
  }
  const handleSectionChange = (_v, _o, section) => {
    onChange({ ...data, section, sectionToYear: null, sectionGroup: null })
  }

  const handleSectionGroupChange = (_v, _o, sectionGroup) => {
    onChange({ ...data, sectionGroup, section: null, sectionToYear: null })
  }

  const updateData = key => (value, _, entity) => {
    onChange({ ...data, [key]: isObject(entity) ? entity : value })
  }

  const handleYearlyChange = () => {
    if (isYearly) {
      setIsYearly(false)
      onChange({ ...data, endDate: new Quota(data).endDate })
    } else {
      setIsYearly(true)
      onChange({ ...data, endDate: data.startDate.clone().add(5, 'years') })
    }
  }

  const totalClassName = useMemo(() => {
    let className = 'input-color-default'

    if (maxTotal !== null) {
      if (data.total > maxTotal) {
        className = 'input-color-chaotic input-background-chaotic'
      } else if (data.total === maxTotal) {
        className = 'input-color-neutral input-background-neutral'
      } else {
        className = 'input-color-good input-background-good'
      }
    }

    return className + ' input-fw-b'
  }, [data.total, maxTotal])

  return (
    <div className='ant-row-mb10'>
      <QuickDualDatePickers
        startDate={data[START_DATE]}
        endDate={data[END_DATE]}
        period={data.period}
        disabled={disabled}
        disabledEndDate={isYearly}
        onDatesChange={handleDatesChange}
      />
      {mode !== QuotaFormModes.DESIDERATA && mode !== QuotaFormModes.RESTRICTED && (
        <Row>
          <Col span={COLSPAN_VALUE}>
            <h4>
              <b>
                {t('common.is_yearly.dd')}
              </b>
            </h4>
          </Col>
          <Col span={COLSPAN_VALUE}>
            <Switch disabled={disabled} title={t('common.is_yearly')} onChange={handleYearlyChange} />
          </Col>
        </Row>
      )}
      {mode !== QuotaFormModes.NULL && (
        <Row>
          <Col span={COLSPAN_VALUE}>
            <h4>
              <b>
                {t('quota_form.places_to_allocate.dd')}
              </b>
            </h4>
          </Col>
          <Col span={COLSPAN_VALUE}>
            <div className={totalClassName}>
              <InputNumber
                disabled={disabled}
                min={0}
                value={data[TOTAL]}
                onChange={updateData(TOTAL)}
              />
            </div>
          </Col>
        </Row>
      )}
      <Row>
        <Col span={COLSPAN_VALUE}>
          <h4>
            <b>
              {t('common.school.dd')}
            </b>
          </h4>
        </Col>
        <Col span={COLSPAN_VALUE}>
          <SchoolsSelector
            disabled={disabled || mode === QuotaFormModes.DESIDERATA || mode === QuotaFormModes.RESTRICTED}
            style={SELECT_STYLE}
            multiple={false}
            values={getIdOrNull(data[SCHOOL])}
            onChange={updateData(SCHOOL)}
            empty
          />
        </Col>
      </Row>
      {mode === QuotaFormModes.DESIDERATA && (
        <Row>
          <Col span={COLSPAN_VALUE}>
            <h4>
              <b>
                {t('common.sector.dd')}
              </b>
            </h4>
          </Col>
          <Col span={COLSPAN_VALUE}>
            <SectorsSelector
              disabled={disabled || disabledSector}
              sectors={sectors}
              style={SELECT_STYLE}
              multiple={false}
              values={getIdOrNull(data[SECTOR])}
              onChange={updateData(SECTOR)}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col span={COLSPAN_VALUE}>
          <h4>
            <b>
              {t('common.section_group.dd')}
            </b>
          </h4>
        </Col>
        <Col span={COLSPAN_VALUE}>
          <SectionGroupsSelector
            disabled={disabled || !data[SECTOR]}
            multiple='default'
            style={SELECT_STYLE}
            sectionGroups={localSectionGroups}
            values={getIdOrNull(data[SECTION_GROUP])}
            onChange={handleSectionGroupChange}
            empty
          />
        </Col>
      </Row>
      <Row>
        <Col span={COLSPAN_VALUE}>
          <h4>
            <b>
              {t('common.section.dd')}
            </b>
          </h4>
        </Col>
        <Col span={COLSPAN_VALUE}>
          <SectionsSelector
            disabled={disabled || !data[SECTOR]}
            highlightValues={mode === QuotaFormModes.DESIDERATA ? sectionsAndYears.sections : []}
            multiple='default'
            style={SELECT_STYLE}
            sections={mode === QuotaFormModes.DESIDERATA ? schoolSections.list : sectionsAndYears.sections}
            values={getIdOrNull(data[SECTION])}
            onChange={handleSectionChange}
            empty
          />
        </Col>
      </Row>
      <Row>
        <Col span={COLSPAN_VALUE}>
          <h4>
            <b>
              {t('common.school_year.dd')}
            </b>
          </h4>
        </Col>
        <Col span={COLSPAN_VALUE}>
          <SectionToYearSelector
            style={SELECT_STYLE}
            disabled={!data.section || disabled}
            highlightValues={mode === QuotaFormModes.DESIDERATA ? sectionsAndYears.years[data.section?.id] : []}
            multiple={false}
            section={data.section}
            sectionToYears={mode === QuotaFormModes.DESIDERATA ? schoolSections.sectionYears[data.section?.id] : sectionsAndYears.years[data.section?.id]}
            values={getIdOrNull(data[SECTION_TO_YEAR])}
            onChange={updateData(SECTION_TO_YEAR)}
            empty
          />
        </Col>
      </Row>
    </div>
  )
}

export default QuotaForm
